import { PrismicRichText } from '@prismicio/react';
import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import { supportedTypes } from '@remote-com/mushu/src/components/Form/DynamicForm/helpers';
import {
  SuccessTitle,
  SuccessDescription,
  SuccessActions,
} from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, ButtonInline } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import { registerLead } from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  SubsribeSuccessContainer,
  ModalTitle,
  FormError,
  SubmitButtonWrapper,
  ModalDescription,
} from 'remote-com-shared/components/ModalForm/styles';
import { getFields } from 'remote-com-shared/domains/hubspot/fields';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { ThemeProvider } from 'styled-components';

import {
  FormWrapper,
  GatedContentSectionWrapper,
  MainContent,
  SectionTitle,
  Description,
  GDPRFormWrapper,
} from './styles';

import { fieldHasContent } from '@/src/domains/prismic/helpers';
import { defaultPrismicComponents } from '@/src/domains/prismic/render';

export function generateAndDownloadDocument(fileUrl) {
  const downloadLink = document.createElement('a');

  downloadLink.href = fileUrl;
  downloadLink.download = true;
  downloadLink.rel = 'noopener';
  downloadLink.click();
}

const GatedContentForm = ({
  hubspotFormDefinition,
  gatedFile,
  gatedFileDownloadLabel,
  formTitle,
  formDescription,
}) => {
  const { submitText, inlineMessage: successMessage, formFieldGroups } = hubspotFormDefinition;

  const fields = getFields(formFieldGroups).map((field) => {
    const { name } = field;

    switch (name) {
      case 'notes':
        return {
          ...field,
          type: supportedTypes.TEXTAREA,
          placeholder: '',
        };

      default:
        return field;
    }
  });

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  const reCaptchaRef = useRef(null);

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      try {
        const payload = {
          ...values,
        };

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotFormDefinition.portalId}/${hubspotFormDefinition.guid}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
        });

        setShowLoader(false);
        setShowSuccessState(true);
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
      <GlobalStyles />
      <FormContainer>
        {showSuccessState ? (
          <SubsribeSuccessContainer>
            <SuccessMark />
            <SuccessTitle>Thank you!</SuccessTitle>
            <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
            <SuccessActions>
              {gatedFile && (
                <Button href={gatedFile.url} target={gatedFile.target}>
                  {gatedFileDownloadLabel}
                </Button>
              )}
            </SuccessActions>
          </SubsribeSuccessContainer>
        ) : (
          <GDPRFormWrapper>
            {formTitle && <ModalTitle>{formTitle}</ModalTitle>}
            {formDescription && <ModalDescription>{formDescription}</ModalDescription>}

            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}

            <DynamicForm
              formName={hubspotFormDefinition.guid}
              fields={fields}
              fieldValues={formValues}
              formError={formState.errorMessage}
              formTheme="dark"
              formSize="small"
              handleOnSubmit={handleSubmit}
              isLoading={formState.isLoading}
              enableReinitialize
            />

            <CaptchaWrapper>
              <LocaleContextConsumer>
                {(locale) => (
                  <ReCAPTCHA
                    hl={locale}
                    ref={reCaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                    onChange={handleCaptchaChange}
                  />
                )}
              </LocaleContextConsumer>
            </CaptchaWrapper>

            <SubmitButtonWrapper>
              <SubmitButton
                isLoading={showLoader}
                disabled={!isCaptchaDone}
                form={hubspotFormDefinition.guid}
              >
                {submitText}
              </SubmitButton>
            </SubmitButtonWrapper>
          </GDPRFormWrapper>
        )}
      </FormContainer>
    </ThemeProvider>
  );
};

const GatedContent = (props) => (
  <FormWrapper>
    <GatedContentForm {...props} />
  </FormWrapper>
);

export const GatedContentSection = ({ title, description, ...formProps }) => (
  <GatedContentSectionWrapper>
    <ContentContainer>
      <MainContent>
        {fieldHasContent(title) && (
          <SectionTitle $tone="dark">
            <PrismicRichText field={title} />
          </SectionTitle>
        )}
        {fieldHasContent(description) && (
          <Description $tone="dark">
            <PrismicRichText field={description} components={defaultPrismicComponents} />
          </Description>
        )}
      </MainContent>
      <GatedContent {...formProps} />
    </ContentContainer>
  </GatedContentSectionWrapper>
);
